import type { ClientListResponse } from '../../types/dtos';

/** Chaves típicas de payload de botão / quick reply — não vêm do cadastro de cliente. */
export function isLikelyButtonPayloadKey(key: string): boolean {
  const n = key.trim().toLowerCase();
  if (/^payload_\d+$/i.test(key.trim())) return true;
  return (
    n === 'fazer_analise' ||
    n === 'sem_interesse' ||
    n === 'bloquear' ||
    n === 'esclarecer_duvidas'
  );
}

export function classifyTemplateKeys(keys: string[]): {
  clientFillable: string[];
  manualOnly: string[];
} {
  const clientFillable: string[] = [];
  const manualOnly: string[] = [];
  for (const k of keys) {
    if (isLikelyButtonPayloadKey(k)) manualOnly.push(k);
    else clientFillable.push(k);
  }
  return { clientFillable, manualOnly };
}

function normKey(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

function setIfNonEmpty(out: Record<string, string>, key: string, value: string | undefined | null): void {
  const v = value != null ? String(value).trim() : '';
  if (v) out[key] = v;
}

/** Razão social; se vazia no cadastro, usa nome fantasia (comum em listagens mínimas). */
function primaryBusinessName(client: ClientListResponse): string | undefined {
  const r = client.razaoSocial?.trim();
  if (r) return r;
  const f = client.nomeFantasia?.trim();
  return f || undefined;
}

/** Normaliza chave de template ou cabeçalho CSV para comparação (acentos, case). */
export function normalizeTemplateOrColumnKey(s: string): string {
  return normKey(s);
}

/**
 * Cabeçalhos de CSV equivalentes à variável do template na validação (além do nome exato da chave).
 * Ex.: variável EMPRESA aceita coluna razao_social / nome_fantasia.
 */
export function csvColumnAliasesForTemplateKey(templateKey: string): string[] {
  const nk = normKey(templateKey);
  if (nk === 'empresa' || nk === 'nome da empresa' || nk === 'empresaaqui' || nk === 'empresaqui') {
    return [
      'razao social',
      'razao_social',
      'razaosocial',
      'nome fantasia',
      'nome_fantasia',
      'nomefantasia',
      'fantasia',
    ];
  }
  if (nk === 'razao social' || nk === 'razaosocial' || nk === 'razao_social') {
    return ['nome fantasia', 'nome_fantasia', 'nomefantasia', 'fantasia', 'empresa', 'nome da empresa'];
  }
  if (nk === 'nome fantasia' || nk === 'nome_fantasia' || nk === 'fantasia') {
    return ['razao social', 'razao_social', 'razaosocial', 'empresa'];
  }
  return [];
}

/**
 * Preenche `fields` do disparo a partir do cadastro quando o nome da chave bate com aliases conhecidos.
 * Heurística Meta: com `body_1`+`body_2` → CNPJ + razão social; só `body_1` → razão social (ex.: boas-vindas).
 */
export function mapClientToTemplateFields(
  client: ClientListResponse,
  templateKeys: string[]
): Record<string, string> {
  const out: Record<string, string> = {};
  const keySet = new Set(templateKeys);
  const hasBody1 = keySet.has('body_1');
  const hasBody2 = keySet.has('body_2');

  for (const key of templateKeys) {
    if (isLikelyButtonPayloadKey(key)) continue;

    if (key === 'body_1') {
      if (hasBody2) setIfNonEmpty(out, key, client.cnpj);
      else setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }
    if (key === 'body_2' && hasBody1) {
      setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }
    if (key === 'body_2' && !hasBody1) {
      setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }

    const nk = normKey(key);

    if (nk === 'cnpj' || nk === 'cnpj da empresa' || nk.includes('cnpj')) {
      setIfNonEmpty(out, key, client.cnpj);
      continue;
    }

    if (
      nk === 'razao social' ||
      nk === 'razao_social' ||
      nk === 'razaosocial' ||
      key === 'RAZÃO SOCIAL'
    ) {
      setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }

    if (nk === 'nome do cliente' || nk === 'nome cliente' || key === 'NOME DO CLIENTE') {
      setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }

    if (nk === 'nome' || key === 'NOME') {
      setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }

    if (
      nk === 'empresa' ||
      nk === 'nome da empresa' ||
      key === 'EMPRESA' ||
      key === 'NOME DA EMPRESA' ||
      key === 'EMPRESAQUI' ||
      key === 'EMPRESAAQUI'
    ) {
      setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }

    if (key === 'header_1' || nk === 'header_1') {
      setIfNonEmpty(out, key, primaryBusinessName(client));
      continue;
    }
  }

  return out;
}

/**
 * Metadados do cadastro que alimentam o fallback Chatwoot (/cliente, /linhas)
 * mesmo quando NÃO são variáveis do template Zenvia.
 * Nomes alinhados aos aliases em CsvClientEnrichmentService / ZenviaDbContextAdapter.
 */
export function buildClienteWhatsAppMetadataFields(client: ClientListResponse): Record<string, string> {
  const out: Record<string, string> = {};
  setIfNonEmpty(out, 'cnpj', client.cnpj);
  setIfNonEmpty(out, 'razao_social', primaryBusinessName(client));
  setIfNonEmpty(out, 'email_adm', client.email);
  setIfNonEmpty(out, 'telefone_adm', client.telefone1);

  if (client.acessosAtivos != null && client.acessosAtivos > 0) {
    out['acessos'] = String(client.acessosAtivos);
  }

  if (client.linhas && client.linhas.length > 0) {
    for (let i = 0; i < client.linhas.length && i < 20; i++) {
      const num = client.linhas[i].numeroLinha?.replace(/\D/g, '');
      if (num) out[`LINHA ${i + 1}`] = num;
    }
  }

  return out;
}

export function normalizeCampaignPhoneDigits(raw: string): string {
  return raw.replace(/\D/g, '');
}
